import React from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { Row, Col, Image } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { pageNames } from 'src/routes/pathParams'
import isArray from 'lodash/isArray'
import { SubCategories } from '../SubCategories'
import { getCategoryLinks } from '../../deps'
import {
  enableNewFeature,
  IS_BROWSER,
  parseMenuCategory,
  application,
  getAbsoluteURLForLegacy,
  i18nTranslate,
  serializeBreadcrumbPath,
  writeBreadcrumbSessionRecord,
  getBreadcrumbSessionRegion,
  isExternalUrl,
  normalizeExternalUrl,
} from 'src/utils'
import { staticDropDownLinks } from '../../NuskinHeader/staticDropDownLinks'
import { PageLink } from '../../PageLink'
import rightArrow from 'src/images/rightarrow.png'
import '../MainMenuSection/styles.scss'
import { getMenuData } from 'src/models/ContentStack/transform'

@observer
class CategoryMenu extends React.Component {
  @observable currentCategory = ''
  @observable activeCategoryBySub = ''
  @observable isChildBeingHovered = false

  hardCodedCategoryIds = [
    'nutrition_kits_and_bundles',
    'product_lines_kits_and_bundles',
    'product_lines_promotions',
  ]

  categoryWraper = ''
  mainCategoryWrapper = ''

  // Cache processed data to avoid recomputation
  processedCategories = null
  cachedStaticLinks = null
  cachedFallbackCategories = null

  isDropDownStyle = false

  static defaultProps = {
    className: '',
  }
  componentDidMount() {
    if (typeof window === 'object') {
      this.handleClickOutside = event => {
        if (
          this.categoryWraper &&
          !this.categoryWraper?.contains(event.target)
        ) {
          this.handleToggle()
        }
      }
      window.addEventListener('click', this.handleClickOutside)
      window.addEventListener('keydown', this.hideSubCategory)
      window.addEventListener('keydown', this.handleTabNavigation, true)
    }
  }
  componentWillUnmount() {
    if (IS_BROWSER) {
      window?.removeEventListener('click', this.handleClickOutside)
      window?.removeEventListener('keydown', this.hideSubCategory)
      window?.removeEventListener('keydown', this.handleTabNavigation, true)
    }
    // Clear refs and cached data to prevent memory leaks
    this.categoryWraper = null
    this.mainCategoryWrapper = null
    this.processedCategories = null
    this.cachedStaticLinks = null
    this.cachedFallbackCategories = null
  }

  handleTabNavigation = event => {
    if (event.key !== 'Tab') return

    if (!this.currentCategory) return

    const activeElement = document.activeElement

    const isOnCategoryButton =
      this.mainCategoryWrapper?.contains(activeElement) &&
      activeElement.classList?.contains('category-link')

    if (isOnCategoryButton && this.categoryWraper) {
      if (event.shiftKey) {
        return
      }
      event.preventDefault()
      event.stopPropagation()

      const firstFocusable = this.categoryWraper.querySelector(
        '[tabindex="0"], a, button'
      )
      if (firstFocusable) {
        firstFocusable.focus()
      }
      return
    }

    const isInDropdown =
      this.categoryWraper && typeof this.categoryWraper.contains === 'function'
        ? this.categoryWraper.contains(activeElement)
        : false

    if (isInDropdown) {
      const focusableElements = Array.from(
        this.categoryWraper.querySelectorAll(
          '[tabindex="0"], a, button, input, select, textarea, [href]'
        )
      ).filter(el => {
        return el.tabIndex >= 0 && !el.disabled && el.offsetParent !== null
      })

      const firstFocusable = focusableElements[0]
      const lastFocusable = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && activeElement === firstFocusable) {
        event.preventDefault()
        event.stopPropagation()

        const { triggerButton } = this.getCategoryTriggerButton()
        if (triggerButton) {
          triggerButton.focus()
        }
        return
      }

      if (!event.shiftKey && activeElement === lastFocusable) {
        event.preventDefault()
        event.stopPropagation()

        const { categoryButtons, triggerIndex } =
          this.getCategoryTriggerButton()

        if (triggerIndex >= 0) {
          const nextButton = categoryButtons[triggerIndex + 1]
          if (nextButton) {
            nextButton.focus()
          } else {
            this.currentCategory = ''
            this.activeCategoryBySub = ''
          }
        }
      }
    }
  }

  getCategoryTriggerButton = () => {
    const categoryButtons = Array.from(
      this.mainCategoryWrapper?.querySelectorAll('.category-link') || []
    )
    const triggerIndex = categoryButtons.findIndex(btn => {
      const categoryClass = Array.from(btn.classList).find(
        c => c !== 'category-link' && c !== 'd-md-block'
      )
      return categoryClass === this.currentCategory
    })
    return {
      categoryButtons,
      triggerIndex,
      triggerButton: categoryButtons[triggerIndex],
    }
  }

  componentDidUpdate(prevProps) {
    const navigationData = this.props?.navigationData || {}
    const prevNavigationData = prevProps?.navigationData || {}

    if (
      Object.keys(navigationData).length > 0 &&
      (this.processedCategories || navigationData !== prevNavigationData)
    ) {
      this.processedCategories = null
      this.cachedStaticLinks = null
      this.cachedFallbackCategories = null
    }
  }

  /**
   * Convert SSR-provided categoryResponse (catalogs/categories/top API format)
   * to the navigationData.categories format used by the redesigned menu.
   * This provides immediate navigation content on first paint while the
   * ContentStack nuskin_header data loads client-side.
   */
  convertCategoryResponseToCategories = (categoryResponse = []) => {
    if (this.cachedFallbackCategories) {
      return this.cachedFallbackCategories
    }

    const fallbackCategories = categoryResponse.map(cat => {
      const subCategories = (cat?.subCategories || [])
        .filter(sub => sub?.properties?.name)
        .map(sub => ({
          label: sub?.properties?.name || '',
          items: (sub?.subCategories || [])
            .filter(item => item?.properties?.name)
            .map(item => ({
              title: item?.properties?.name || '',
              href: getCategoryLinks(item, false) || '#',
            })),
        }))

      return {
        label: cat?.properties?.name || '',
        identifier: cat?.identifier || '',
        subCategories,
        isDummyCategory: cat?.isDummyCategory || false,
        href: cat?.isDummyCategory
          ? null
          : getCategoryLinks(cat, false) || null,
        isLink: cat?.isDummyCategory ? false : !cat?.subCategories?.length,
      }
    })

    this.cachedFallbackCategories = fallbackCategories
    return fallbackCategories
  }

  hideSubCategory = event => {
    if (event?.key === 'Escape') {
      this.currentCategory = ''
      this.activeCategoryBySub = ''
      this.isChildBeingHovered = false
    }
  }

  handleToggle = (categoryId = '') => {
    if (categoryId) {
      this.currentCategory = categoryId
    }
  }

  handleChildItemHover = isHovering => {
    this.isChildBeingHovered = isHovering
  }

  storeCategoryNavigationPath = (
    categoryLabel,
    categoryLink,
    parentPath = []
  ) => {
    if (typeof sessionStorage === 'undefined') return

    const homeBreadcrumb = {
      label: i18nTranslate('breadCrumb.home', 'Home', {
        nameSpace: 'ssr-resource',
      }),
      link: '/',
    }

    const currentBreadcrumb = {
      label: categoryLabel,
      link: categoryLink,
    }

    const navigationPath = [homeBreadcrumb, ...parentPath, currentBreadcrumb]

    const serializedPath = serializeBreadcrumbPath(navigationPath)
    const currentLocale = getBreadcrumbSessionRegion()

    writeBreadcrumbSessionRecord('cs_nav_path', serializedPath, currentLocale)
  }

  isCatalogNavigationLink = link =>
    /\/(catalog|categoryLanding)\//i.test(link || '')

  captureCatalogBreadcrumb = (title, href, parentBreadcrumb) => {
    if (
      enableNewFeature('enableContentStackBreadcrumb') &&
      href &&
      this.isCatalogNavigationLink(href)
    ) {
      this.storeCategoryNavigationPath(title, href, [parentBreadcrumb])
    }
  }

  renderStaticDropDownLinks = (dropdownMenuList = {}) => {
    // Use cached result if available
    if (this.cachedStaticLinks) {
      return this.cachedStaticLinks
    }

    const lists = staticDropDownLinks() || []
    let list = lists?.filter(
      list => list?.parentId === this.currentCategory?.replaceAll?.('_', '')
    )

    // Use Set for faster lookups
    const hardCodedSet = new Set(this.hardCodedCategoryIds)

    dropdownMenuList?.subCategories?.forEach(subCategory => {
      if (hardCodedSet.has(subCategory?.identifier || '')) {
        list?.push({
          linkInfo: getCategoryLinks(subCategory, false),
          label: parseMenuCategory(subCategory?.properties?.name || ''),
          isPlpCategoryAndNotLegacyPage: true,
          identifier: subCategory?.identifier || '',
        })
      }
    })

    if (list?.length > 0) {
      const result = list?.map((item, index) => {
        if (item?.isPlpCategoryAndNotLegacyPage) {
          return <></>
        } else {
          return <></>
        }
      })

      // Cache the result to avoid recomputation
      this.cachedStaticLinks = result
      return result
    }
  }

  renderSubCategory = (subCategory, index) => {
    const hasSubcategories = subCategory?.subCategories?.length > 0
    if (this.hardCodedCategoryIds?.includes(subCategory?.identifier)) {
      return <></>
    }
    const hasImage = ''
    return (
      <Col
        key={`subcategory-${index}`}
        className={`${
          hasSubcategories || !hasImage ? 'col-2' : 'col'
        } subcategory-wrapper mx-xxl-3 mx-lg-3`}>
        <div
          tabIndex={0}
          className="categorymenu-subcategory-box"
          onMouseEnter={e =>
            e?.target && (e.target.style.backgroundColor = '#ededed')
          }
          onMouseLeave={e =>
            e?.target && (e.target.style.backgroundColor = 'transparent')
          }>
          <div className="categorymenu-subcategory-header">
            <Image
              className="categorymenu-right-arrow"
              src={rightArrow}
              height={20}
              width={20}
            />
          </div>
        </div>
        <SubCategories
          handleToggle={this.handleToggle}
          subCategory={subCategory || []}
        />
      </Col>
    )
  }

  renderJumpToRow = categoryLabel => {
    const navigationData = this.props?.navigationData || {}
    const category = navigationData?.categories?.find(
      cat => cat?.label === categoryLabel
    )
    if (!category) return null
    const jumpToSub = category?.subCategories?.find(sub =>
      sub?.label?.toLowerCase()?.includes('jump')
    )
    if (!jumpToSub || !jumpToSub?.items || jumpToSub?.items?.length === 0)
      return null
    const parentBreadcrumb = {
      label:
        category?.properties?.name || category?.label || categoryLabel || '',
      link: category?.href || '',
    }
    const isLegacyURLOpenInSameTab =
      navigationData?.isLegacyURLOpenInSameTab || false
    const legacyURLList = navigationData?.legacyURLList || []
    return (
      <Row className="categorymenu-jumpto-row">
        <Col xs="auto">
          <p className="categorymenu-jumpto-label">Jump To:</p>
        </Col>
        {jumpToSub?.items?.map((item, idx) => (
          <Col xs="auto" key={idx} className="categorymenu-jumpto-item-col">
            {typeof item === 'string' ? (
              <span className="categorymenu-jumpto-item">{item}</span>
            ) : (
              (() => {
                let href = item?.href || '#'
                const isLegacyURL = legacyURLList.some(val =>
                  href.includes(val)
                )
                href = getAbsoluteURLForLegacy(isLegacyURL, href)
                const isExternalLink = isExternalUrl(href)
                return isExternalLink ? (
                  <a
                    href={normalizeExternalUrl(href)}
                    className="categorymenu-jumpto-item"
                    target={
                      isLegacyURLOpenInSameTab && isLegacyURL
                        ? '_self'
                        : '_blank'
                    }
                    rel="noopener noreferrer"
                    onClick={e => {
                      if (href === '#') {
                        e.preventDefault()
                      }
                      this.captureCatalogBreadcrumb(
                        item?.title,
                        href,
                        parentBreadcrumb
                      )
                    }}>
                    {item?.title}
                  </a>
                ) : (
                  <Link
                    to={href}
                    className="categorymenu-jumpto-item"
                    target={href?.includes('site') ? '_blank' : '_self'} // for india site should open in new tab
                    onClick={e => {
                      if (href === '#') {
                        e.preventDefault()
                      }
                      this.captureCatalogBreadcrumb(
                        item?.title,
                        href,
                        parentBreadcrumb
                      )
                    }}>
                    {item?.title}
                  </Link>
                )
              })()
            )}
          </Col>
        ))}
      </Row>
    )
  }
  // Optimized event handlers to reduce inline function creation
  handleMouseEnter = categoryId => () => {
    const navigationData = this.props?.navigationData || {}
    const categories = navigationData?.categories || []

    const processedCategories = categories?.map(cat => {
      return {
        identifier:
          cat?.identifier || cat?.label?.replace(/\s+/g, '_')?.toLowerCase(),
        properties: { name: cat?.label },
        subCategories: cat?.subCategories || [],
        isDummyCategory: cat?.isDummyCategory || false,
        href: cat?.href || null,
        isLink: cat?.isLink || false,
      }
    })

    const category = processedCategories?.find(
      cat => cat?.identifier === categoryId
    )

    this.currentCategory = categoryId
    if (!category?.isLink && category?.subCategories?.length > 0) {
      this.activeCategoryBySub = categoryId
    }
  }

  handleContainerMouseLeave = () => {
    this.activeCategoryBySub = ''
    this.currentCategory = ''
  }

  handleMenuItemClick = () => {
    this.currentCategory = ''
    this.activeCategoryBySub = ''
    this.isChildBeingHovered = false
  }

  renderCategoryLink = (
    isLink,
    isExternalLink,
    hasSubCategories,
    isDummyCategory,
    propsForComponent,
    categoryName
  ) => {
    if (isLink) {
      if (isExternalLink) {
        return <a {...propsForComponent}>{categoryName}</a>
      }
      return <Link {...propsForComponent}>{categoryName}</Link>
    }
    if (hasSubCategories || isDummyCategory) {
      return (
        <span {...propsForComponent} style={{ cursor: 'default' }}>
          {categoryName}
        </span>
      )
    }
    return <Link {...propsForComponent}>{categoryName}</Link>
  }

  renderSubCategoryItem = (
    item,
    { isLegacyURL, isExternalItem, isLegacyURLOpenInSameTab, handleItemClick }
  ) => {
    if (typeof item === 'string') {
      return (
        <span
          className="categorymenu-item-text"
          onClick={this.handleMenuItemClick}>
          {item}
        </span>
      )
    }
    if (isLegacyURLOpenInSameTab && isLegacyURL) {
      return (
        <a
          href={item?.href || '#'}
          target="_self"
          className="categorymenu-item-text"
          onClick={handleItemClick}>
          {item?.title}
        </a>
      )
    }
    if (isExternalItem) {
      // external hrefs must bypass the router, which
      // would prepend the locale basename
      return (
        <a
          href={normalizeExternalUrl(item?.href)}
          target="_blank"
          rel="noopener noreferrer"
          className="categorymenu-item-text"
          onClick={handleItemClick}>
          {item?.title}
        </a>
      )
    }
    return (
      <Link
        to={item?.href || '#'}
        target={item?.href?.includes('site') ? '_blank' : '_self'} // for india site should open in new tab
        className="categorymenu-item-text"
        onClick={handleItemClick}>
        {item?.title}
      </Link>
    )
  }

  renderCategoryList = (category, index) => {
    const categoryId = category?.identifier || ''
    const categoryName =
      category?.properties?.name || category?.label || category?.title || ''
    const hasSubCategories =
      isArray(category?.subCategories) && category?.subCategories?.length > 0
    const isLink = category?.isLink || false

    const handleCategoryClick = e => {
      if (hasSubCategories && !isLink) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }

      if (
        enableNewFeature('enableContentStackBreadcrumb') &&
        category?.href &&
        this.isCatalogNavigationLink(category?.href)
      ) {
        this.storeCategoryNavigationPath(categoryName, category.href)
      }
    }

    const handleFocus = () => {
      if (hasSubCategories && !isLink) {
        this.currentCategory = categoryId
        this.activeCategoryBySub = categoryId
      }
    }

    const handleBlur = () => {
      setTimeout(() => {
        const activeElement = document.activeElement
        const mainWrapper = this.mainCategoryWrapper
        const dropdownElement = this.categoryWraper
        const focusStillInNav = mainWrapper?.contains(activeElement)
        const focusInDropdown = dropdownElement?.contains(activeElement)
        if (!focusStillInNav && !focusInDropdown) {
          this.currentCategory = ''
          this.activeCategoryBySub = ''
        }
      }, 100)
    }

    const submenuId = `category-submenu-${categoryId}`
    const propsForComponent = {
      className: `category-link d-md-block ${category?.identifier || ''}`,
      role: 'button',
      'data-testid': 'qa-departments',
      tabIndex: 0,
      onMouseOver: this.handleMouseEnter(categoryId),
      onClick: handleCategoryClick,
      onFocus: handleFocus,
      onBlur: handleBlur,
      ...(hasSubCategories && !isLink
        ? {
            'aria-haspopup': 'true',
            'aria-expanded': this.currentCategory === categoryId,
            'aria-controls': submenuId,
          }
        : {}),
    }

    const isExternalLink =
      category?.href?.startsWith('http://') ||
      category?.href?.startsWith('https://')

    if (isLink && category?.href) {
      if (isExternalLink) {
        propsForComponent.href = category?.href
        propsForComponent.tag = 'a'
      } else {
        propsForComponent.to = category?.href
      }
    } else if (!hasSubCategories && !category?.isDummyCategory) {
      propsForComponent.to = `${getCategoryLinks(category, false)}`
    }

    const isActive =
      !!this.activeCategoryBySub && this.activeCategoryBySub === categoryId

    return (
      <div
        key={`category-${index}`}
        className={`newcurrent-category flex-shrink-0${isActive ? ' active' : ''}${isActive && this.isChildBeingHovered ? ' child-hovered' : ''} ${
          this.isDropDownStyle ? 'position-relative' : ''
        }`}>
        {this.renderCategoryLink(
          isLink,
          isExternalLink,
          hasSubCategories,
          category?.isDummyCategory,
          propsForComponent,
          categoryName
        )}
        {this.isDropDownStyle &&
          this.currentCategory === categoryId &&
          hasSubCategories &&
          !isLink && (
            <section
              id={submenuId}
              aria-label={categoryName}
              className="position-absolute sub-category-wrapper dropdown-style py-2 px-5 d-flex flex-column"
              ref={element => (this.categoryWraper = element)}>
              {category?.subCategories?.map((subCategory, idx) => (
                <div key={idx} className="mb-2">
                  <div
                    className="mega-menu-subcategory-title categorymenu-subcategory-title"
                    tabIndex={0}
                    aria-label={subCategory?.label}
                    onMouseEnter={() => {
                      this.activeCategoryBySub = categoryId
                      this.handleChildItemHover(true)
                    }}
                    onFocus={() => {
                      this.activeCategoryBySub = categoryId
                      this.handleChildItemHover(true)
                    }}
                    onMouseLeave={() => {
                      this.activeCategoryBySub = ''
                      this.handleChildItemHover(false)
                    }}
                    onBlur={() => {
                      this.activeCategoryBySub = ''
                      this.handleChildItemHover(false)
                    }}>
                    <span className="categorymenu-subcategory-title-text">
                      {subCategory?.label}
                    </span>
                  </div>
                  {subCategory?.items && (
                    <ul className="mega-menu-items categorymenu-items">
                      {subCategory?.items?.map((item, i) => (
                        <li
                          key={i}
                          className="mega-menu-item categorymenu-item"
                          role="menuitem"
                          aria-label={
                            typeof item === 'string' ? item : item?.title
                          }>
                          {typeof item === 'string' ? item : item?.title}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>
          )}
      </div>
    )
  }

  renderNewJumpToSectionForDesktop = dropdownMenuList => {
    const isLegacyURLOpenInSameTab =
      this.props?.navigationData?.isLegacyURLOpenInSameTab || false
    const legacyURLList = this.props?.navigationData?.legacyURLList || []
    const parentBreadcrumb = {
      label:
        dropdownMenuList?.properties?.name || dropdownMenuList?.label || '',
      link: dropdownMenuList?.href || '',
    }
    return dropdownMenuList?.subCategories
      ?.filter(sub => sub?.label?.toLowerCase()?.includes('jump'))
      ?.map((sub, idx) => (
        <Col key={idx} md={3} sm={6} xs={12} className="mb-3 categorymenu-col">
          <div
            tabIndex={0}
            className="new-jumpTo-section categorymenu-jumpto-label">
            <span>{sub?.label?.split('##')[0]}</span>
          </div>
          <div className="new-jumpTo-items">
            {sub?.items &&
              sub?.items?.map((item, i) => {
                let href = item?.href || '#'
                const isLegacyURL = legacyURLList.some(val =>
                  href.includes(val)
                )
                href = getAbsoluteURLForLegacy(isLegacyURL, href)
                const isExternalLink = isExternalUrl(href)
                return (
                  <button key={i} tabIndex={-1} className="new-jumpTo-item">
                    {typeof item === 'string' ? (
                      <span className="new-categorymenu-jumpto-item">
                        {item}
                      </span>
                    ) : isExternalLink ? (
                      <a
                        href={normalizeExternalUrl(href)}
                        className="new-categorymenu-jumpto-item"
                        target={
                          isLegacyURLOpenInSameTab && isLegacyURL
                            ? '_self'
                            : '_blank'
                        }
                        rel="noopener noreferrer"
                        onClick={e => {
                          if (href === '#') {
                            e.preventDefault()
                          }
                          this.captureCatalogBreadcrumb(
                            item?.title,
                            href,
                            parentBreadcrumb
                          )
                        }}>
                        {item?.title}
                      </a>
                    ) : (
                      <Link
                        to={href}
                        className="new-categorymenu-jumpto-item"
                        target={href?.includes('site') ? '_blank' : '_self'}
                        onClick={e => {
                          if (href === '#') {
                            e.preventDefault()
                          }
                          this.captureCatalogBreadcrumb(
                            item?.title,
                            href,
                            parentBreadcrumb
                          )
                        }}>
                        {item?.title}
                      </Link>
                    )}
                  </button>
                )
              })}
          </div>
        </Col>
      ))
  }

  calculateHeaderPosition = () => {
    let headerClass = 'full-view'
    if (application?.scroll?.vertical > 40) {
      headerClass = 'compact-view'
    }
    let stickyTop = application?.scroll?.vertical

    return { headerType: headerClass, yPosition: stickyTop }
  }

  render() {
    const navigationData = this.props?.navigationData || {}
    let categories = navigationData?.categories || []

    // Fallback: use SSR-provided categoryResponse when ContentStack
    // navigationData hasn't loaded yet (avoids empty header on first paint)
    const categoryResponse = this.props?.categoryResponse
    if (
      !categories.length &&
      Array.isArray(categoryResponse) &&
      categoryResponse.length > 0
    ) {
      categories = this.convertCategoryResponseToCategories(categoryResponse)
    }

    let stickyness = this.calculateHeaderPosition()
    const isLegacyURLOpenInSameTab =
      navigationData?.isLegacyURLOpenInSameTab || false
    const legacyURLList = navigationData?.legacyURLList || []
    this.processedCategories = categories?.map(cat => {
      return {
        identifier:
          cat?.identifier || cat?.label?.replace(/\s+/g, '_')?.toLowerCase(),
        properties: { name: cat?.label },
        subCategories: cat?.subCategories || [],
        isDummyCategory: cat?.isDummyCategory || false,
        href: cat?.href || null,
        isLink: cat?.isLink || false,
      }
    })

    const lists = this.processedCategories
    const isListNonEmpty = isArray(lists) && lists?.length > 0
    const dropdownMenuList =
      isListNonEmpty &&
      lists?.find(list => list?.identifier === this.currentCategory)
    const enableNewJumpToSection = enableNewFeature('enableNewJumpToSection')
    return (
      <div
        className={`d-flex flex-column align-items-center flex-wrap category-wrap-middle ${this.isDropDownStyle ? 'dropdown-style-category' : ''}`}
        onMouseLeave={this.handleContainerMouseLeave}>
        <div
          className="d-flex category-wrapper"
          ref={element => (this.mainCategoryWrapper = element)}>
          {lists?.map(this.renderCategoryList)}
        </div>
        {dropdownMenuList?.subCategories?.length > 0 &&
          !dropdownMenuList?.isLink &&
          !this.isDropDownStyle && (
            <section
              className={
                'position-absolute ' +
                (enableNewFeature('enableRedesignMainMenu')
                  ? `redesign-sub-category-wrapper ${stickyness.headerType} `
                  : '') +
                'sub-category-wrapper categorymenu-dropdown'
              }
              id={`category-submenu-${dropdownMenuList?.identifier}`}
              aria-label={
                dropdownMenuList?.properties?.name ||
                dropdownMenuList?.label ||
                ''
              }
              ref={element => (this.categoryWraper = element)}>
              <Row
                noGutters
                className={`list-wrap mt-3 mb-3  ${this.currentCategory !== 'ourimpact' ? ' w-100' : ''} categorymenu-row`}>
                {enableNewJumpToSection && (
                  <>{this.renderNewJumpToSectionForDesktop(dropdownMenuList)}</>
                )}
                {dropdownMenuList?.subCategories
                  ?.filter(sub => !sub?.label?.toLowerCase()?.includes('jump'))
                  ?.map((sub, idx) => (
                    <Col
                      key={idx}
                      md={3}
                      sm={6}
                      xs={12}
                      className="mb-3 categorymenu-col">
                      <div
                        className="mega-menu-subcategory-title categorymenu-subcategory-title"
                        tabIndex={0}
                        aria-label={sub?.label}
                        onMouseEnter={() => {
                          this.activeCategoryBySub =
                            dropdownMenuList?.identifier
                          this.handleChildItemHover(true)
                        }}
                        onFocus={() => {
                          this.activeCategoryBySub =
                            dropdownMenuList?.identifier
                          this.handleChildItemHover(true)
                        }}
                        onMouseLeave={() => {
                          this.activeCategoryBySub = ''
                          this.handleChildItemHover(false)
                        }}
                        onBlur={() => {
                          this.activeCategoryBySub = ''
                          this.handleChildItemHover(false)
                        }}>
                        <span className="categorymenu-subcategory-title-text">
                          {sub?.label}
                        </span>
                        <Image
                          className="categorymenu-right-arrow"
                          src={rightArrow}
                          height={20}
                          width={20}
                          alt=""
                          aria-hidden="true"
                        />
                      </div>
                      <ul className="mega-menu-items categorymenu-items">
                        {sub?.items &&
                          sub?.items?.map((item, i) => {
                            const isLegacyURL = legacyURLList.some(val =>
                              item?.href?.includes(val)
                            )
                            const isExternalItem = isExternalUrl(item?.href)
                            const handleItemClick = e => {
                              if (!item?.href) {
                                e.preventDefault()
                              }
                              // COMX-1439: Store navigation path for subcategory
                              if (
                                enableNewFeature(
                                  'enableContentStackBreadcrumb'
                                ) &&
                                item?.href &&
                                this.isCatalogNavigationLink(item?.href)
                              ) {
                                const parentBreadcrumb = {
                                  label:
                                    dropdownMenuList?.properties?.name || '',
                                  link: dropdownMenuList?.href || '',
                                }
                                this.storeCategoryNavigationPath(
                                  item?.title,
                                  item?.href,
                                  [parentBreadcrumb]
                                )
                              }
                              this.handleMenuItemClick()
                            }
                            return (
                              <li
                                key={i}
                                className="mega-menu-item categorymenu-item"
                                role="menuitem"
                                aria-label={
                                  typeof item === 'string' ? item : item?.title
                                }
                                onMouseEnter={() => {
                                  this.activeCategoryBySub =
                                    dropdownMenuList?.identifier
                                  this.handleChildItemHover(true)
                                }}
                                onFocus={() => {
                                  this.activeCategoryBySub =
                                    dropdownMenuList?.identifier
                                  this.handleChildItemHover(true)
                                }}
                                onMouseLeave={() => {
                                  this.activeCategoryBySub = ''
                                  this.handleChildItemHover(false)
                                }}
                                onBlur={() => {
                                  this.activeCategoryBySub = ''
                                  this.handleChildItemHover(false)
                                }}>
                                {this.renderSubCategoryItem(item, {
                                  isLegacyURL,
                                  isExternalItem,
                                  isLegacyURLOpenInSameTab,
                                  handleItemClick,
                                })}
                              </li>
                            )
                          })}
                      </ul>
                    </Col>
                  ))}
              </Row>
              {!enableNewJumpToSection && (
                <div className="w-100 mb-2">
                  {this.renderJumpToRow(dropdownMenuList?.properties?.name)}
                </div>
              )}
            </section>
          )}
      </div>
    )
  }
}

export default CategoryMenu
export { CategoryMenu }
